import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { useCurrentShopLocalesAvailables } from "@/lib/translator.tsx";
import getHTMLTextContent from "@/lib/utils/dom/get-html-text-content.ts";
import { cn } from "@/utils/cn.ts";
import { DisabledReasonContainer } from "@babackapp/react-hook-forms-components/form-input-container";
import useTranslation, {
  Translation,
} from "@babackapp/translations/use-translation";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@babackapp/ui/components/hover-card";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { LetterText } from "lucide-react";
import { PropsWithChildren, useMemo } from "react";
import { CustomKey } from "./custom-message-settings-form.tsx";
import { UpdateCustomMessageDialog } from "./update-custom-message-dialog.tsx";

type CustomMessagesItemProps = {
  customKey: CustomKey;
  disabled?: string | boolean;
};

export function CustomMessageItem({
  customKey,
  disabled,
}: CustomMessagesItemProps) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const values = shop.preferences.userPortal[customKey];
  const languages = useCurrentShopLocalesAvailables();

  const missingLanguages = useMemo(() => {
    return languages
      ?.filter((lang) => {
        const value = values?.find((value) => value.lang === lang.code);
        const content = value?.text ? getHTMLTextContent(value.text) : null;
        if (!content?.trim().length) return true;
        return false;
      })
      .map((e) => e.code);
  }, [values, languages]);

  const contentToDisplay = values
    ?.map((value) => getHTMLTextContent(value.text))
    .find(Boolean);

  return (
    <UpdateCustomMessageDialog customKey={customKey} disabled={!!disabled}>
      <DisabledReasonContainer disabled={disabled}>
        <MissingLangHoverCard languageCodes={missingLanguages ?? []}>
          <div
            className={cn(
              "border border-border rounded-md items-center gap-4 pr-6 p-4 flex",
              disabled && "cursor-not-allowed opacity-40",
            )}
          >
            {missingLanguages?.length ? (
              <ExclamationTriangleIcon className="text-destructive size-6" />
            ) : (
              <LetterText className="text-muted-foreground size-6" />
            )}
            <div className="flex flex-col gap-1 flex-1">
              <span className="text-sm font-bold text-left">
                {t(`settings.customMessage.${customKey}` as Translation)}
              </span>
              {contentToDisplay && (
                <p className="text-muted-foreground text-xs text-left">
                  {contentToDisplay}
                </p>
              )}
            </div>
          </div>
        </MissingLangHoverCard>
      </DisabledReasonContainer>
    </UpdateCustomMessageDialog>
  );
}

type MissingLangHoverCardProps = {
  languageCodes: string[];
};

function MissingLangHoverCard({
  languageCodes,
  children,
}: PropsWithChildren<MissingLangHoverCardProps>) {
  const t = useTranslation();
  if (!languageCodes.length) return children;

  return (
    <HoverCard>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent align="start" className="z-30 absolute">
        <div className="flex gap-4 w-56">
          <ExclamationTriangleIcon className="text-yellow-500 h-8 w-8" />
          <p className="text-xs font-medium text-muted-foreground leading-snug">
            {t("settings.customMessage.notTranslated", {
              languages: languageCodes.join(", "),
            })}
          </p>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
